import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Star, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { shopInfo } from '../data/menuData';

const HeroSection = () => {
  const { t } = useLanguage();

  const scrollToMenu = () => {
    const element = document.querySelector('#menu');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_d979dc90-af0b-4d56-bedb-da718efe5edd/artifacts/luxx6acm_unnamed%20%2838%29.webp')`
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#1A1816]/85" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            {/* Halal Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border-2 border-[#D4AF37] text-[#D4AF37] px-4 py-2 rounded-full mb-6"
              data-testid="halal-badge"
            >
              <span className="text-lg">☪</span>
              <span className="font-manrope font-bold text-sm tracking-wide">{t('hero.halal')}</span>
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="font-manrope text-[#9F3E1F] font-semibold text-lg mb-4"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* Main Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-[#FDFBF7] leading-tight mb-6"
              data-testid="hero-title"
            >
              {t('hero.title')}
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="font-manrope text-[#FDFBF7]/70 text-lg mb-8 max-w-lg mx-auto lg:mx-0"
            >
              {t('hero.description')}
            </motion.p>

            {/* Info Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-wrap gap-4 justify-center lg:justify-start mb-8"
            >
              <div className="flex items-center gap-2 bg-[#2D2A26]/80 backdrop-blur px-4 py-2 rounded-xl">
                <Star className="text-[#D4AF37] fill-[#D4AF37]" size={18} />
                <span className="font-manrope font-semibold text-[#FDFBF7]">{shopInfo.rating}</span>
                <span className="font-manrope text-[#FDFBF7]/60">({shopInfo.reviewCount} reviews)</span>
              </div>
              <div className="flex items-center gap-2 bg-[#2D2A26]/80 backdrop-blur px-4 py-2 rounded-xl">
                <Clock className="text-[#9F3E1F]" size={18} />
                <span className="font-manrope text-[#FDFBF7]">{shopInfo.hours.open} - {shopInfo.hours.close}</span>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <motion.button
                onClick={scrollToMenu}
                className="bg-[#9F3E1F] text-white px-8 py-4 rounded-full font-manrope font-semibold text-lg hover:bg-[#8A351A] transition-colors shadow-lg shadow-[#9F3E1F]/20"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                data-testid="view-menu-btn"
              >
                {t('hero.cta')}
              </motion.button>
              <motion.a
                href={shopInfo.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 border-2 border-[#FDFBF7] text-[#FDFBF7] px-8 py-4 rounded-full font-manrope font-semibold text-lg hover:bg-[#FDFBF7] hover:text-[#1A1816] transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                data-testid="directions-btn"
              >
                <MapPin size={20} />
                {t('contact.directions')}
              </motion.a>
            </motion.div>
          </motion.div>

          {/* Right Content - Image Grid */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              {/* Main Large Image */}
              <motion.div
                className="col-span-2 relative rounded-2xl overflow-hidden shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_d979dc90-af0b-4d56-bedb-da718efe5edd/artifacts/do6n55yx_unnamed%20%2842%29.webp"
                  alt="Delicious Döner Plate"
                  className="w-full h-64 md:h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </motion.div>

              {/* Smaller Images */}
              <motion.div
                className="relative rounded-2xl overflow-hidden shadow-xl"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_d979dc90-af0b-4d56-bedb-da718efe5edd/artifacts/4ze5vqwr_unnamed%20%2839%29.webp"
                  alt="AK-AL Shop"
                  className="w-full h-40 md:h-48 object-cover"
                />
              </motion.div>

              <motion.div
                className="relative rounded-2xl overflow-hidden shadow-xl"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_d979dc90-af0b-4d56-bedb-da718efe5edd/artifacts/srcpvony_unnamed%20%2841%29.webp"
                  alt="Turkish Delights"
                  className="w-full h-40 md:h-48 object-cover"
                />
              </motion.div>
            </div>

            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, type: 'spring' }}
              className="absolute -bottom-4 -left-4 bg-[#2D2A26] rounded-2xl shadow-xl p-4 border border-[#FDFBF7]/10"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xl">✓</span>
                </div>
                <div>
                  <p className="font-manrope font-bold text-[#FDFBF7]">{t('hero.openNow')}</p>
                  <p className="font-manrope text-sm text-[#FDFBF7]/60">{t('hero.delivery')}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="flex justify-center mt-16"
        >
          <motion.button
            onClick={scrollToMenu}
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-[#9F3E1F]"
            data-testid="scroll-indicator"
          >
            <ChevronDown size={32} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
